import { Button } from "@/components/ui/button";
import { Megaphone, PenLine, Mail, Rocket, BarChart3, MessageSquare, Newspaper } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const MarketingAndOutreachPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO 
        title="AI-Powered Marketing Tools for Nonprofits" 
        description="Create engaging content, grow your audience, and amplify your mission with AI-powered marketing tools designed for nonprofits."
      />
      <div className="min-h-screen bg-gradient-to-b from-[#F2FCE2] to-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
          <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
          <div className="container mx-auto px-4 relative">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Marketing & Outreach 
              </h1>
              <p className="text-xl md:text-2xl text-gray-100 mb-1">
                Amplify Your Nonprofit’s Voice with AI-Powered Content.
              </p>
            </div>
          </div>
        </div>

        {/* Challenges Section */}
        <div className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                The Challenges Nonprofits Face with Marketing & Outreach
              </h2>
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <BarChart3 className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Time Constraints</h3>
                  <p className="text-gray-600">
                    Marketing takes time, and many teams lack dedicated staff to create and manage outreach campaigns.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <PenLine className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Content Fatigue</h3>
                  <p className="text-gray-600">
                    Writing fresh, compelling posts, emails, and newsletters week after week is challenging.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="text-[#2D7D46] mb-4">
                    <Megaphone className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Low Social Engagement</h3>
                  <p className="text-gray-600">
                    Posts often fail to gain traction due to a lack of compelling storytelling or visuals.
                  </p>
                </div>
              </div>
              <div className="text-center bg-[#2D7D46]/5 rounded-xl p-8">
                <p className="text-lg text-gray-700 italic">
                  "Organizations using AI-generated content see 50% higher engagement rates on social media and a 30% increase in email open rates."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Solutions Section */}
        <div className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              AI-Powered Marketing Tools for Nonprofits
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <Megaphone className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Social Media Post Generator</h3>
                <p className="text-gray-600 mb-4">
                  Create engaging, mission-driven social media posts for multiple platforms. See 50% higher engagement rates.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Platform-optimized content</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Mission-aligned messaging</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Instant generation</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <Newspaper className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Newsletter & Email Updates</h3>
                <p className="text-gray-600 mb-4">
                  Keep supporters informed with AI-generated email campaigns that strengthen relationships.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Personalized content</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Consistent updates</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Higher open rates</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="p-3 rounded-lg bg-[#2D7D46]/10 text-[#2D7D46] w-fit mb-4">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-4">AI Chatbot for Donors</h3>
                <p className="text-gray-600 mb-4">
                  Handle donor questions, event sign-ups, and impact updates in real-time without staff intervention.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>24/7 support</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Instant responses</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D7D46]" />
                    <span>Lead Management</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Stats Section */}
        <div className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              The Impact of AI on Nonprofit Marketing
            </h2>
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">50%</div>
                <p className="text-gray-600">Increase in social shares</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">30%</div>
                <p className="text-gray-600">Higher email open rates</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">60%</div>
                <p className="text-gray-600">More visual engagement</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2D7D46] mb-2">5-7</div>
                <p className="text-gray-600">Hours saved per week</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-20 bg-[#2D7D46] text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Get Started with AI-Powered Marketing Today
            </h2>
            <p className="text-xl mb-12 max-w-2xl mx-auto">
              Join hundreds of nonprofits using AI to create engaging content, save time, and amplify their mission.
            </p>
            <div className="space-y-4">
              <Button 
                size="lg"
                className="bg-white text-[#2D7D46] hover:bg-gray-100 text-lg px-8 py-6 h-auto w-full md:w-auto"
                onClick={() => navigate("/signup")}
              >
                <Rocket className="w-5 h-5 mr-2" />
                Generate Your First Social Media Post
              </Button>
              <div className="mt-4">
                <Link 
                  to="/npo/tools/marketing" 
                  className="text-white hover:underline inline-flex items-center"
                >
                  Explore More Marketing Tools →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarketingAndOutreachPage;
